import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import {
  getApiMessage,
  isHitLabClientEnabled,
  isHttpsSongUrl,
} from './hitLabUtils';
import './hitLab.css';

const INITIAL_FORM = {
  artistName: '',
  cityRegion: '',
  contactEmail: '',
  contactPhone: '',
  songTitle: '',
  genreLane: '',
  releaseStatus: '',
  bestContactOrSocialUrl: '',
  whyThisSong: '',
  hookTimestamp: '',
  songPrivateUrl: '',
  cleanExplicit: '',
  originalWork: false,
  noCovers: false,
  noUnclearedSamples: false,
  noStolenBeat: false,
  producerRightsClear: false,
  featuresApproved: false,
  authorizedSubmitter: false,
  privateReviewPlayback: false,
  futurePublicPromotion: false,
  futureStreetDropConsideration: false,
  contactAboutOpportunities: false,
};

const REQUIRED_RIGHTS = [
  ['originalWork', 'I own or control the submitted recording and composition rights.'],
  ['noCovers', 'This is not a cover.'],
  ['noUnclearedSamples', 'This song has no uncleared samples.'],
  ['noStolenBeat', 'This beat is not stolen.'],
  ['producerRightsClear', 'Producer rights are clear for private HitLab review.'],
  ['featuresApproved', 'All featured artists approved this submission.'],
  ['authorizedSubmitter', 'I am authorized to submit this song.'],
  ['privateReviewPlayback', 'I allow private HitLab review playback.'],
];

export default function HitLabSubmit() {
  const location = useLocation();
  const checkoutSuccess = useMemo(
    () => new URLSearchParams(location.search || '').get('checkout') === 'success',
    [location.search],
  );
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const clientEnabled = isHitLabClientEnabled();

  const loadIntake = () =>
    axiosConn.get('hitlab/intake/me').then((response) => {
      const next = response.data?.data?.intake || null;
      setIntake(next);
      setForm((current) => ({
        ...current,
        artistName: current.artistName || next?.artistName || '',
        cityRegion: current.cityRegion || next?.cityRegion || '',
        contactEmail: current.contactEmail || next?.contactEmail || '',
        contactPhone: current.contactPhone || next?.contactPhone || '',
        songTitle: current.songTitle || next?.songTitle || '',
        genreLane: current.genreLane || next?.genreLane || '',
      }));
      return next;
    });

  useEffect(() => {
    if (!clientEnabled) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    loadIntake()
      .catch((err) => {
        if (active) {
          setError(
            getApiMessage(
              err,
              'Artist intake status could not be loaded. Please try again.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientEnabled]);

  useEffect(() => {
    if (!checkoutSuccess || intake?.paymentStatus === 'paid') return undefined;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      loadIntake().catch(() => {});
      if (attempts >= 5) window.clearInterval(id);
    }, 3000);
    return () => window.clearInterval(id);
  }, [checkoutSuccess, intake?.paymentStatus]);

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const canSubmit = intake?.paymentStatus === 'paid' && intake?.status === 'paid_pending_upload';

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!isHttpsSongUrl(form.songPrivateUrl)) {
      setError('Private song link must start with https://');
      return;
    }
    const missingRight = REQUIRED_RIGHTS.find(([key]) => !form[key]);
    if (missingRight) {
      setError('Required rights confirmations must be accepted.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axiosConn.post('hitlab/intake/submit', form);
      setIntake(response.data?.data?.intake || null);
      setSuccess('Song submitted. HitLab review status is now recorded.');
    } catch (err) {
      setError(
        getApiMessage(err, 'Submission could not be saved. Please try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="hitlab-page hitlab-workflow">
      <Helmet>
        <title>JG HitLab Song Submission</title>
      </Helmet>
      <section className="hitlab-shell hitlab-submit-shell">
        <div className="hitlab-workflow-copy">
          <p className="hitlab-kicker">SONG SUBMISSION</p>
          <h1>Send the one record you want heard.</h1>
          <p className="hitlab-lede">
            Use a private HTTPS link that Joe Galaxy and HitLab reviewers can open.
          </p>
        </div>

        {!clientEnabled ? (
          <div className="hitlab-alert">Artist intake is not accepting submissions right now.</div>
        ) : loading ? (
          <div className="hitlab-alert">Loading payment status...</div>
        ) : !canSubmit ? (
          <div className="hitlab-alert">
            <strong>
              {intake?.paymentStatus === 'paid'
                ? 'Submission already received'
                : 'Payment pending'}
            </strong>
            <span>
              {intake?.paymentStatus === 'paid'
                ? ` Current status: ${intake?.status || 'in review'}.`
                : ' We are waiting for Stripe confirmation before song submission opens.'}
            </span>
            {intake?.paymentStatus !== 'paid' ? (
              <Link to="/hitlab/intake">Return to intake checkout</Link>
            ) : null}
          </div>
        ) : (
          <form className="hitlab-form" onSubmit={submit}>
            {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
            {success ? <div className="hitlab-alert hitlab-alert-success">{success}</div> : null}
            <div className="hitlab-form-grid">
              <label>
                Artist name
                <input name="artistName" maxLength="120" value={form.artistName} onChange={updateField} required />
              </label>
              <label>
                City / region
                <input name="cityRegion" maxLength="120" value={form.cityRegion} onChange={updateField} required />
              </label>
              <label>
                Contact email
                <input name="contactEmail" type="email" maxLength="254" value={form.contactEmail} onChange={updateField} required />
              </label>
              <label>
                Contact phone
                <input name="contactPhone" maxLength="40" value={form.contactPhone} onChange={updateField} />
              </label>
              <label>
                Song title
                <input name="songTitle" maxLength="160" value={form.songTitle} onChange={updateField} required />
              </label>
              <label>
                Genre lane
                <input name="genreLane" maxLength="80" value={form.genreLane} onChange={updateField} required />
              </label>
              <label>
                Release status
                <input name="releaseStatus" maxLength="80" value={form.releaseStatus} onChange={updateField} placeholder="Unreleased, released, demo, mastered" />
              </label>
              <label>
                Best contact or social URL
                <input name="bestContactOrSocialUrl" maxLength="500" value={form.bestContactOrSocialUrl} onChange={updateField} />
              </label>
              <label className="hitlab-form-wide">
                Private HTTPS song link
                <input name="songPrivateUrl" maxLength="2048" value={form.songPrivateUrl} onChange={updateField} placeholder="https://..." required />
              </label>
              <label>
                Hook timestamp
                <input name="hookTimestamp" maxLength="32" value={form.hookTimestamp} onChange={updateField} placeholder="0:42" />
              </label>
              <label>
                Clean / explicit
                <input name="cleanExplicit" maxLength="40" value={form.cleanExplicit} onChange={updateField} />
              </label>
              <label className="hitlab-form-wide">
                Why this song
                <textarea name="whyThisSong" maxLength="1000" value={form.whyThisSong} onChange={updateField} />
              </label>
            </div>

            <fieldset className="hitlab-rights">
              <legend>Rights confirmations</legend>
              {REQUIRED_RIGHTS.map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    name={key}
                    checked={form[key]}
                    onChange={updateField}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            <fieldset className="hitlab-rights hitlab-rights-optional">
              <legend>Optional future permissions</legend>
              <label>
                <input
                  type="checkbox"
                  name="futurePublicPromotion"
                  checked={form.futurePublicPromotion}
                  onChange={updateField}
                />
                <span>Contact me separately about possible public promotion.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  name="futureStreetDropConsideration"
                  checked={form.futureStreetDropConsideration}
                  onChange={updateField}
                />
                <span>Contact me separately about possible street-drop consideration.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  name="contactAboutOpportunities"
                  checked={form.contactAboutOpportunities}
                  onChange={updateField}
                />
                <span>Contact me about future JG music opportunities.</span>
              </label>
            </fieldset>

            <button type="submit" className="hitlab-primary-btn" disabled={submitting}>
              {submitting ? 'Submitting song...' : 'Submit song for HitLab review'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
